<template>
  <!-- 纯功能组件，无UI渲染 -->
  <div></div>
</template>

<script>
import { Message, Notification, MessageBox } from 'element-ui';

export default {
  name: 'FileManager',
  props: {
    // 是否自动检测浏览器兼容性
    autoDetect: {
      type: Boolean,
      default: true
    },
    // 默认文件编码
    defaultEncoding: {
      type: String,
      default: 'utf-8',
      validator: (val) => ['utf-8', 'utf-16', 'gbk'].includes(val)
    }
  },
  data() {
    return {
      // 浏览器兼容性状态
      browserSupport: {
        supported: false,
        browserName: null,
        checked: false
      },
      // 支持的浏览器列表
      supportedBrowsers: [
        {
          name: 'Google Chrome',
          minVersion: 86,
          detect: (ua) => ua.includes('Chrome') && !ua.includes('Edg'),
          downloadUrl: 'https://www.google.com/chrome/'
        },
        {
          name: 'Microsoft Edge',
          minVersion: 86,
          detect: (ua) => ua.includes('Edg'),
          downloadUrl: 'https://www.microsoft.com/zh-cn/edge'
        }
      ],
      // 当前操作的目录句柄
      currentDirHandle: null,
      // 当前目录的显示路径（非实际系统路径，出于安全限制）
      currentDirDisplayPath: '',
      // 文件句柄缓存，用于快速访问
      fileHandleCache: new Map(),
      // 当前选中的文件信息
      selectedFile: null
    };
  },
  mounted() {
    // 自动检测浏览器兼容性
    if (this.autoDetect) {
      this.checkBrowserSupport();
    }
  },
  methods: {
    /**
     * 检查浏览器是否支持文件系统API
     * @returns {Promise<boolean>} 是否支持
     */
    checkBrowserSupport() {
      return new Promise((resolve) => {
        // 检测核心API支持
        const isSupported = 'showDirectoryPicker' in window &&
            'showOpenFilePicker' in window &&
            'FileSystemFileHandle' in window;

        // 检测浏览器类型
        let browserName = null;
        const userAgent = navigator.userAgent;

        for (const browser of this.supportedBrowsers) {
          if (browser.detect(userAgent)) {
            browserName = browser.name;
            break;
          }
        }

        // 更新支持状态
        this.browserSupport = {
          supported: isSupported,
          browserName,
          checked: true
        };

        // 不支持时显示提示
        if (!isSupported) {
          this._showBrowserSupportError();
        }

        resolve(isSupported);
      });
    },

    /**
     * 显示浏览器不支持的错误提示
     */
    _showBrowserSupportError() {
      const supportedList = this.supportedBrowsers
          .map(b => `${b.name} ${b.minVersion}+`)
          .join('、');

      MessageBox.alert(
          `<p>当前浏览器不支持文件系统操作功能</p>
         <p>请使用以下浏览器的最新版本：</p>
         <p>${supportedList}</p>
         <p class="el-alert__description">注：功能仅支持HTTPS或localhost环境</p>`,
          '浏览器不支持',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '我知道了',
            type: 'error'
          }
      ).catch(() => {});
    },

    /**
     * 检查操作权限和环境
     * @returns {Promise<void>}
     */
    async _checkPermission() {
      if (!this.browserSupport.checked) {
        await this.checkBrowserSupport();
      }

      if (!this.browserSupport.supported) {
        throw new Error('当前浏览器不支持文件系统操作');
      }

      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        throw new Error('文件操作仅支持HTTPS或localhost环境');
      }
    },

    /**
     * 1. 手动选择文件夹路径，获取实际路径信息
     * @returns {Promise<Object>} 包含文件夹信息的对象
     */
    async selectDirectory() {
      await this._checkPermission();

      try {
        // 显示文件夹选择对话框
        const dirHandle = await window.showDirectoryPicker({
          mode: 'readwrite'
        });

        // 存储当前目录句柄
        this.currentDirHandle = dirHandle;
        this.currentDirDisplayPath = dirHandle.name;
        this.fileHandleCache.clear();

        // 返回文件夹信息（注意：无法获取真实系统路径，出于安全限制）
        const result = {
          name: dirHandle.name,
          displayPath: dirHandle.name,
          isDirectory: true,
          handle: dirHandle
        };

        Notification.success({
          title: '文件夹选择成功',
          message: `已选择：${dirHandle.name}`
        });

        this.$emit('directory-selected', result);
        return result;
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error('用户取消了文件夹选择');
        }
        Message.error(`选择文件夹失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 2. 获取选中文件夹下的文件树及文件信息
     * @param {FileSystemDirectoryHandle} [dirHandle] - 可选，指定目录句柄
     * @param {String} [relativePath] - 相对路径，用于递归
     * @returns {Promise<Array>} 文件树数组
     */
    async getFileTree(dirHandle, relativePath = '') {
      await this._checkPermission();

      // 默认使用当前选中的目录
      const targetDir = dirHandle || this.currentDirHandle;
      if (!targetDir) {
        throw new Error('请先选择文件夹');
      }

      try {
        const fileTree = [];
        // 遍历目录 entries
        for await (const [name, handle] of targetDir.entries()) {
          // 构建相对路径
          const currentRelativePath = relativePath
              ? `${relativePath}/${name}`
              : name;

          // 基础信息
          const item = {
            name,
            relativePath: currentRelativePath,
            isDirectory: handle.kind === 'directory'
          };

          // 如果是目录，递归获取子文件树
          if (handle.kind === 'directory') {
            item.children = await this.getFileTree(handle, currentRelativePath);
            item.fileCount = this._countFiles(item.children);
            item.size = this._calculateDirectorySize(item.children);
            item.sizeFormatted = this._formatFileSize(item.size);
          }
          // 如果是文件，获取详细信息
          else {
            const file = await handle.getFile();
            item.size = file.size;
            item.sizeFormatted = this._formatFileSize(file.size);
            item.lastModified = file.lastModified;
            item.lastModifiedFormatted = new Date(file.lastModified).toLocaleString();
            item.type = file.type;
            item.extension = this._getExtension(name);

            // 缓存文件句柄，便于后续操作
            this.fileHandleCache.set(currentRelativePath, handle);
          }

          fileTree.push(item);
        }

        // 排序：目录在前，文件在后，按名称排序
        fileTree.sort((a, b) => {
          if (a.isDirectory && !b.isDirectory) return -1;
          if (!a.isDirectory && b.isDirectory) return 1;
          return a.name.localeCompare(b.name);
        });

        this.$emit('file-tree-loaded', fileTree);
        return fileTree;
      } catch (err) {
        Message.error(`获取文件树失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 3. 根据文件路径+名称获取文件内容
     * @param {String} filePath - 文件相对路径（包含文件名）
     * @param {String} [encoding] - 编码格式，默认使用组件配置
     * @returns {Promise<String>} 文件内容
     */
    async getFileContent(filePath, encoding = this.defaultEncoding) {
      await this._checkPermission();

      if (!this.currentDirHandle) {
        throw new Error('请先选择文件夹');
      }

      try {
        // 获取文件句柄
        const fileHandle = await this._getHandleByPath(filePath);

        if (!fileHandle || fileHandle.kind !== 'file') {
          throw new Error(`文件不存在：${filePath}`);
        }

        // 读取文件内容
        const file = await fileHandle.getFile();
        let content;

        if (encoding === 'utf-8') {
          content = await file.text();
        } else {
          // 处理其他编码
          const buffer = await file.arrayBuffer();
          const decoder = new TextDecoder(encoding);
          content = decoder.decode(buffer);
        }

        // 更新选中文件信息
        this.selectedFile = {
          name: file.name,
          path: filePath,
          size: file.size,
          lastModified: file.lastModified,
          content
        };

        this.$emit('file-content-loaded', {
          path: filePath,
          content,
          fileInfo: this.selectedFile
        });

        return content;
      } catch (err) {
        Message.error(`读取文件失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 4. 在指定路径下创建特定名称的文件
     * @param {String} directoryPath - 目录相对路径
     * @param {String} fileName - 文件名（包含扩展名）
     * @param {String} [content] - 初始内容
     * @returns {Promise<Object>} 新创建的文件信息
     */
    async createFile(directoryPath, fileName, content = '') {
      await this._checkPermission();

      if (!this.currentDirHandle) {
        throw new Error('请先选择文件夹');
      }

      if (!fileName.includes('.')) {
        throw new Error('文件名必须包含扩展名（如：file.txt）');
      }

      try {
        // 获取目标目录句柄
        const dirHandle = await this._getHandleByPath(directoryPath, true);

        if (!dirHandle || dirHandle.kind !== 'directory') {
          throw new Error(`目录不存在：${directoryPath}`);
        }

        // 检查文件是否已存在
        try {
          await dirHandle.getFileHandle(fileName, { create: false });
          throw new Error(`文件已存在：${fileName}`);
        } catch (err) {
          if (err.name !== 'NotFoundError') {
            throw err;
          }
        }

        // 创建新文件
        const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });

        // 写入初始内容
        if (content) {
          const writable = await fileHandle.createWritable();
          await writable.write(content);
          await writable.close();
        }

        // 获取文件信息
        const file = await fileHandle.getFile();
        const filePath = directoryPath
            ? `${directoryPath}/${fileName}`
            : fileName;

        // 更新缓存
        this.fileHandleCache.set(filePath, fileHandle);

        const result = {
          name: fileName,
          path: filePath,
          size: file.size,
          lastModified: file.lastModified,
          created: true
        };

        Message.success(`文件创建成功：${filePath}`);
        this.$emit('file-created', result);

        // 通知文件树已更新
        this.getFileTree().then(tree => {
          this.$emit('file-tree-updated', tree);
        });

        return result;
      } catch (err) {
        Message.error(`创建文件失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 5. 修改特定文件的指定行内容
     * @param {String} filePath - 文件相对路径
     * @param {Number} startLine - 开始行号（1-based）
     * @param {Number} endLine - 结束行号（1-based）
     * @param {String|Array<String>} newContent - 替换内容（字符串或行数组）
     * @returns {Promise<Object>} 修改结果
     */
    async modifyFileLines(filePath, startLine, endLine, newContent) {
      await this._checkPermission();

      if (!this.currentDirHandle) {
        throw new Error('请先选择文件夹');
      }

      if (startLine < 1 || endLine < startLine) {
        throw new Error('无效的行号范围');
      }

      try {
        // 读取原文件内容
        const originalContent = await this.getFileContent(filePath);
        const lines = originalContent.split(/\r?\n/);

        // 验证行号有效性
        if (endLine > lines.length) {
          throw new Error(`结束行号超过文件总行数（共${lines.length}行）`);
        }

        // 处理替换内容
        const newLines = Array.isArray(newContent)
            ? newContent
            : newContent.split(/\r?\n/);

        // 执行替换
        lines.splice(startLine - 1, endLine - startLine + 1, ...newLines);
        const modifiedContent = lines.join('\n');

        // 获取文件句柄并写入
        const fileHandle = await this._getHandleByPath(filePath);
        const writable = await fileHandle.createWritable({ keepExistingData: false });
        await writable.write(modifiedContent);
        await writable.close();

        // 获取更新后的文件信息
        const file = await fileHandle.getFile();

        const result = {
          path: filePath,
          startLine,
          endLine,
          originalLines: lines.slice(startLine - 1, endLine),
          newLines,
          lineCountChanged: newLines.length - (endLine - startLine + 1),
          lastModified: file.lastModified
        };

        Message.success(`文件修改成功：${filePath}`);
        this.$emit('file-lines-modified', result);
        return result;
      } catch (err) {
        Message.error(`修改文件失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 6. 根据路径和文件名删除文件
     * @param {String} filePath - 文件相对路径
     * @returns {Promise<Boolean>} 是否删除成功
     */
    async deleteFile(filePath) {
      await this._checkPermission();

      if (!this.currentDirHandle) {
        throw new Error('请先选择文件夹');
      }

      try {
        // 解析路径，获取文件和父目录句柄
        const lastSlashIndex = filePath.lastIndexOf('/');
        const directoryPath = lastSlashIndex > -1 ? filePath.substring(0, lastSlashIndex) : '';
        const fileName = lastSlashIndex > -1 ? filePath.substring(lastSlashIndex + 1) : filePath;

        // 获取父目录句柄
        const dirHandle = await this._getHandleByPath(directoryPath, true);
        if (!dirHandle || dirHandle.kind !== 'directory') {
          throw new Error(`目录不存在：${directoryPath}`);
        }

        // 确认删除
        await MessageBox.confirm(
            `确定要删除文件"${fileName}"吗？此操作不可恢复。`,
            '确认删除',
            {
              confirmButtonText: '删除',
              cancelButtonText: '取消',
              type: 'error'
            }
        );

        // 执行删除
        await dirHandle.removeEntry(fileName);

        // 更新缓存
        this.fileHandleCache.delete(filePath);

        Message.success(`文件已删除：${filePath}`);
        this.$emit('file-deleted', { path: filePath });

        // 通知文件树已更新
        this.getFileTree().then(tree => {
          this.$emit('file-tree-updated', tree);
        });

        return true;
      } catch (err) {
        if (err.name === 'AbortError' || err.message === 'cancel') {
          throw new Error('用户取消删除');
        }
        Message.error(`删除文件失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 7. 选择文件用于HTTPS上传，获取文件信息和二进制数据
     * @param {String} [directoryPath] - 可选，指定起始目录
     * @param {Array<String>} [acceptTypes] - 可选，接受的文件类型
     * @returns {Promise<Object>} 包含文件上传信息的对象
     */
    async selectFileForUpload(directoryPath = '', acceptTypes = ['.*']) {
      await this._checkPermission();

      try {
        // 构建接受的文件类型配置
        const acceptConfig = {};
        acceptTypes.forEach(type => {
          if (type.startsWith('.')) {
            // 处理文件扩展名
            const mime = this._getMimeTypeByExtension(type);
            if (!acceptConfig[mime]) acceptConfig[mime] = [];
            acceptConfig[mime].push(type);
          } else {
            // 处理MIME类型
            acceptConfig[type] = [];
          }
        });

        // 显示文件选择对话框
        const [fileHandle] = await window.showOpenFilePicker({
          types: [{
            description: '可上传文件',
            accept: acceptConfig
          }],
          multiple: false
        });

        // 获取文件信息
        const file = await fileHandle.getFile();
        const fileBuffer = await file.arrayBuffer();

        // 获取相对路径
        let relativePath = '';
        if (this.currentDirHandle) {
          relativePath = await this._getRelativePath(fileHandle);
        }

        // 准备上传数据
        const uploadData = {
          name: file.name,
          size: file.size,
          sizeFormatted: this._formatFileSize(file.size),
          type: file.type,
          lastModified: file.lastModified,
          lastModifiedFormatted: new Date(file.lastModified).toLocaleString(),
          relativePath: relativePath || file.name,
          buffer: fileBuffer,
          // 用于表单上传的File对象
          fileObject: file,
          // 生成可用于fetch的FormData
          formData: this._createFormData(file)
        };

        this.selectedFile = uploadData;
        Notification.success({
          title: '文件选择成功',
          message: `已选择：${file.name}（${uploadData.sizeFormatted}）`
        });

        this.$emit('file-selected-for-upload', uploadData);
        return uploadData;
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error('用户取消了文件选择');
        }
        Message.error(`选择文件失败：${err.message}`);
        throw err;
      }
    },

    /**
     * 辅助方法：根据路径获取文件/目录句柄
     * @param {String} path - 相对路径
     * @param {Boolean} isDirectory - 是否是目录
     * @returns {Promise<FileSystemHandle>} 文件/目录句柄
     */
    async _getHandleByPath(path, isDirectory = false) {
      if (!path || path === '.') {
        return this.currentDirHandle;
      }

      // 从缓存中获取
      if (!isDirectory && this.fileHandleCache.has(path)) {
        return this.fileHandleCache.get(path);
      }

      // 解析路径
      const pathParts = path.split('/').filter(part => part && part !== '.');
      let currentHandle = this.currentDirHandle;

      try {
        // 遍历路径获取句柄
        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i];
          const isLastPart = i === pathParts.length - 1;

          if (isLastPart && !isDirectory) {
            // 最后一部分是文件
            currentHandle = await currentHandle.getFileHandle(part);
          } else {
            // 中间部分是目录
            currentHandle = await currentHandle.getDirectoryHandle(part);
          }
        }

        return currentHandle;
      } catch (err) {
        throw new Error(`路径不存在：${path}`);
      }
    },

    /**
     * 辅助方法：计算目录大小
     * @param {Array} fileTree - 文件树数组
     * @returns {Number} 目录总大小（字节）
     */
    _calculateDirectorySize(fileTree) {
      let totalSize = 0;

      fileTree.forEach(item => {
        if (item.isDirectory) {
          totalSize += this._calculateDirectorySize(item.children);
        } else {
          totalSize += item.size;
        }
      });

      return totalSize;
    },

    /**
     * 辅助方法：统计文件数量
     * @param {Array} fileTree - 文件树数组
     * @returns {Number} 文件总数
     */
    _countFiles(fileTree) {
      let count = 0;

      fileTree.forEach(item => {
        if (item.isDirectory) {
          count += this._countFiles(item.children);
        } else {
          count++;
        }
      });

      return count;
    },

    /**
     * 辅助方法：格式化文件大小
     * @param {Number} bytes - 字节数
     * @returns {String} 格式化后的大小字符串
     */
    _formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    /**
     * 辅助方法：获取文件扩展名
     * @param {String} fileName - 文件名
     * @returns {String} 扩展名
     */
    _getExtension(fileName) {
      const lastDotIndex = fileName.lastIndexOf('.');
      return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
    },

    /**
     * 辅助方法：根据扩展名获取MIME类型
     * @param {String} extension - 文件扩展名
     * @returns {String} MIME类型
     */
    _getMimeTypeByExtension(extension) {
      const mimeMap = {
        '.txt': 'text/plain',
        '.html': 'text/html',
        '.css': 'text/css',
        '.js': 'application/javascript',
        '.json': 'application/json',
        '.xml': 'application/xml',
        '.pdf': 'application/pdf',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.svg': 'image/svg+xml',
        '.zip': 'application/zip',
        '.gz': 'application/gzip',
        '.tar': 'application/x-tar',
        '.jsx': 'text/jsx',
        '.ts': 'application/typescript',
        '.tsx': 'text/tsx',
        '.py': 'text/x-python',
        '.java': 'text/x-java-source'
      };

      return mimeMap[extension.toLowerCase()] || 'application/octet-stream';
    },

    /**
     * 辅助方法：获取文件相对路径
     * @param {FileSystemFileHandle} fileHandle - 文件句柄
     * @returns {Promise<String>} 相对路径
     */
    async _getRelativePath(fileHandle) {
      let pathParts = [fileHandle.name];
      let currentHandle = await fileHandle.getParent();

      try {
        while (currentHandle !== this.currentDirHandle) {
          pathParts.unshift(currentHandle.name);
          currentHandle = await currentHandle.getParent();
        }
      } catch (err) {
        // 如果文件不在当前目录下，返回文件名
        return fileHandle.name;
      }

      return pathParts.join('/');
    },

    /**
     * 辅助方法：创建FormData对象用于上传
     * @param {File} file - File对象
     * @returns {FormData} FormData实例
     */
    _createFormData(file) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('fileName', file.name);
      formData.append('fileSize', file.size);
      formData.append('fileType', file.type);
      return formData;
    },

    /**
     * 重置组件状态
     */
    reset() {
      this.currentDirHandle = null;
      this.currentDirDisplayPath = '';
      this.fileHandleCache.clear();
      this.selectedFile = null;
      this.$emit('reset');
    }
  },
  /**
   * 提供API供父组件使用
   */
  provide() {
    return {
      fileManager: {
        // 基础方法
        checkBrowserSupport: this.checkBrowserSupport,
        reset: this.reset,

        // 文件夹操作
        selectDirectory: this.selectDirectory,
        getFileTree: this.getFileTree,

        // 文件操作
        getFileContent: this.getFileContent,
        createFile: this.createFile,
        modifyFileLines: this.modifyFileLines,
        deleteFile: this.deleteFile,

        // 上传相关
        selectFileForUpload: this.selectFileForUpload,

        // 工具方法
        formatFileSize: this._formatFileSize,
        getFileExtension: this._getExtension
      }
    };
  }
};
</script>

<style scoped></style>
